import React, { useEffect, useRef, useState } from 'react';
import { FREDOKA, VX, PipStudioHeader, VoiceStyles } from './VoicePicker';

// Record a character's voice (the iOS record sheet on the web): read Pip's five
// lines, at least 35 seconds, then listen back, re-record, or use it. The
// server judges loudness after the ElevenLabs Voice Isolator; the meter here
// is only a hint while recording.

const MIN_SECONDS = 35;
const MAX_SECONDS = 120;

const SCRIPT = [
  "Hi! I'm so happy to see you today — should we go on an adventure together?",
  'Once upon a time, a little star fell right into our backyard, and it was still glowing!',
  'We tiptoed through the garden, past the sleepy sunflowers, all the way to the old oak tree.',
  'Can you believe it? The door was tiny, bright red, and it had a doorbell just for us!',
  'You are so brave and so kind, and I love telling stories with you. The end… or is it?',
];

function pickMimeType() {
  if (typeof MediaRecorder === 'undefined') return '';
  return ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg'].find((m) => MediaRecorder.isTypeSupported(m)) || '';
}

function extensionFor(mime) {
  if (mime.includes('mp4')) return 'm4a';
  if (mime.includes('ogg')) return 'ogg';
  return 'webm';
}

export default function VoiceRecorder({ isOpen, onClose, onDone }) {
  const [phase, setPhase] = useState('idle'); // idle | recording | review
  const [elapsed, setElapsed] = useState(0);
  const [level, setLevel] = useState(0);
  const [error, setError] = useState('');
  const [take, setTake] = useState(null); // { file, url }
  const [listening, setListening] = useState(false);
  const refs = useRef({});

  const teardown = () => {
    const r = refs.current;
    clearInterval(r.timer);
    cancelAnimationFrame(r.raf);
    r.stream?.getTracks().forEach((t) => t.stop());
    r.ctx?.close?.().catch(() => {});
    r.audio?.pause();
    refs.current = {};
  };

  useEffect(() => {
    if (!isOpen) {
      teardown();
      setPhase('idle');
      setElapsed(0);
      setError('');
      setTake(null);
      setListening(false);
    }
    return undefined;
  }, [isOpen]);
  useEffect(() => () => teardown(), []);

  const finish = () => {
    const rec = refs.current.recorder;
    if (rec && rec.state === 'recording') rec.stop();
  };

  const start = async () => {
    setError('');
    setTake(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = pickMimeType();
      const recorder = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      const chunks = [];
      recorder.ondataavailable = (e) => e.data?.size && chunks.push(e.data);
      const startedAt = Date.now();
      recorder.onstop = () => {
        const seconds = (Date.now() - startedAt) / 1000;
        teardown();
        setLevel(0);
        if (seconds < MIN_SECONDS) {
          setPhase('idle');
          setError(`That was ${Math.round(seconds)} seconds — please record at least ${MIN_SECONDS} so the voice comes out great.`);
          return;
        }
        const type = recorder.mimeType || mime || 'audio/webm';
        const blob = new Blob(chunks, { type });
        const file = new File([blob], `voice.${extensionFor(type)}`, { type });
        setTake({ file, url: URL.createObjectURL(blob) });
        setPhase('review');
      };

      // live level meter (a hint only)
      const Ctx = window.AudioContext || window.webkitAudioContext;
      const ctx = Ctx ? new Ctx() : null;
      if (ctx) {
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 1024;
        ctx.createMediaStreamSource(stream).connect(analyser);
        const buf = new Float32Array(analyser.fftSize);
        const tick = () => {
          analyser.getFloatTimeDomainData(buf);
          let sum = 0;
          for (let i = 0; i < buf.length; i += 1) sum += buf[i] * buf[i];
          const db = 20 * Math.log10(Math.sqrt(sum / buf.length) + 1e-9);
          setLevel(Math.max(0, Math.min(1, (db + 50) / 50)));
          refs.current.raf = requestAnimationFrame(tick);
        };
        refs.current.raf = requestAnimationFrame(tick);
      }

      refs.current = { ...refs.current, stream, recorder, ctx };
      refs.current.timer = setInterval(() => {
        const s = (Date.now() - startedAt) / 1000;
        setElapsed(s);
        if (s >= MAX_SECONDS) finish();
      }, 200);
      recorder.start(1000);
      setElapsed(0);
      setPhase('recording');
    } catch (_) {
      setError('Microphone access is needed to record — allow it in your browser settings and try again.');
    }
  };

  const toggleListen = () => {
    if (!take) return;
    if (listening) {
      refs.current.audio?.pause();
      setListening(false);
      return;
    }
    const a = new Audio(take.url);
    a.onended = () => setListening(false);
    refs.current.audio = a;
    a.play().then(() => setListening(true)).catch(() => setListening(false));
  };

  if (!isOpen) return null;
  const pill = {
    fontFamily: FREDOKA,
    fontSize: 15,
    fontWeight: 600,
    color: VX.ink,
    background: '#fff',
    border: `2px solid ${VX.ink}`,
    borderRadius: 14,
    padding: '10px 18px',
    cursor: 'pointer',
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Record a voice"
      onClick={() => phase !== 'recording' && onClose()}
      style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(11,26,51,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
    >
      <VoiceStyles />
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 'min(520px, 100%)',
          maxHeight: 'calc(100vh - 32px)',
          overflowY: 'auto',
          borderRadius: 24,
          border: `2.5px solid ${VX.ink}`,
          boxShadow: `0 6px 0 ${VX.ink}, 0 26px 60px rgba(11,26,51,0.35)`,
          background: '#F0F7FF',
          fontFamily: FREDOKA,
          color: VX.ink,
        }}
      >
        <PipStudioHeader line1="Record your" line2="voice!" height={130} />
        <div style={{ padding: '16px 20px 22px', display: 'flex', flexDirection: 'column', gap: 12, alignItems: 'stretch' }}>
          <p style={{ margin: 0, textAlign: 'center', fontSize: 15, color: VX.inkSoft }}>
            Read Pip's five lines out loud, in your natural voice — close to the mic. Find a quiet spot and we'll polish the rest.
          </p>
          {SCRIPT.map((line, i) => (
            <div key={line} style={{ display: 'flex', gap: 12, alignItems: 'flex-start', background: '#fff', border: `2px solid ${VX.ink}`, borderRadius: 16, padding: '11px 14px' }}>
              <span style={{ flexShrink: 0, width: 26, height: 26, borderRadius: '50%', background: VX.gold, border: `1.8px solid ${VX.ink}`, fontWeight: 700, fontSize: 14, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{i + 1}</span>
              <span style={{ fontSize: 16, lineHeight: 1.35 }}>{line}</span>
            </div>
          ))}

          {error && <div role="alert" style={{ color: '#D12F4A', fontSize: 14, fontWeight: 500, textAlign: 'center' }}>{error}</div>}

          {phase === 'recording' && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '6px 14px', borderRadius: 999, background: 'rgba(175,227,254,0.6)', border: `1.8px solid ${VX.ink}`, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                <span style={{ width: 9, height: 9, borderRadius: '50%', background: '#E5304B' }} />
                {Math.round(elapsed)}s{elapsed < MIN_SECONDS ? ` — keep going, at least ${MIN_SECONDS}s` : ' — great, finish whenever you like'}
              </span>
              <div aria-hidden="true" style={{ display: 'flex', gap: 4, alignItems: 'flex-end', height: 24 }}>
                {Array.from({ length: 14 }).map((_, i) => (
                  <span key={i} style={{ width: 10, height: 8 + i, borderRadius: 2, background: i / 14 < level ? (level > 0.36 ? '#33B85A' : '#E5304B') : 'rgba(11,26,51,0.12)' }} />
                ))}
              </div>
              {level > 0 && level <= 0.36 && <span style={{ color: '#D12F4A', fontSize: 13, fontWeight: 600 }}>Louder please — get closer to the mic!</span>}
            </div>
          )}

          {phase === 'review' ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, alignItems: 'stretch', marginTop: 4 }}>
              <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
                <button type="button" className="vx-press" style={pill} onClick={toggleListen}>{listening ? '■ Stop' : '▶ Listen back'}</button>
                <button type="button" className="vx-press" style={pill} onClick={() => { refs.current.audio?.pause(); setListening(false); setTake(null); setPhase('idle'); }}>↺ Re-record</button>
              </div>
              <button
                type="button"
                className="vx-press"
                onClick={() => { refs.current.audio?.pause(); onDone(take.file); }}
                style={{ ...pill, background: VX.gold, fontSize: 17, fontWeight: 700, boxShadow: '0 5px 0 #E89410', padding: '14px 18px' }}
              >
                Use this voice →
              </button>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, marginTop: 6 }}>
              <button
                type="button"
                className="vx-press"
                aria-label={phase === 'recording' ? 'Finish recording' : 'Start recording'}
                onClick={phase === 'recording' ? finish : start}
                style={{
                  width: 84,
                  height: 84,
                  borderRadius: '50%',
                  border: `2.5px solid ${VX.ink}`,
                  background: phase === 'recording' ? '#E5304B' : VX.blue,
                  boxShadow: `0 5px 0 ${phase === 'recording' ? '#B81D35' : VX.blueDeep}`,
                  color: '#fff',
                  fontSize: 30,
                  cursor: 'pointer',
                }}
              >
                {phase === 'recording' ? '■' : '🎙'}
              </button>
              <span style={{ fontSize: 13.5, color: VX.inkSoft }}>{phase === 'recording' ? 'Tap to finish' : 'Tap to start recording'}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
